import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { booleanToYesNo, genericErrorToast } from '@app/helpers';
import { addDetailsBreadcrumb, setExceptionHandler, getModalSwitch, removeDetailsBreadcrumb } from '@app/store/actions';
import { IAgentDetail, IAgentClientDetails, IPolicyAuth, IWindowsLogonConfig, AgentTypeEnum, IRadiusClient } from '@app/models';
import { agentsService, policiesAuthService } from '@app/services';
import { ModalComponent, IModalData, CopyComponent, PasslyButton } from '@app/components';
import EditAgent, { AgentViewType } from '@app/pages/authManager/dialogs/addAgent.dialog';
import ReadWindowsLogon from '@app/pages/authManager/components/readWindowsLogon.component';
import ReadRadiusConfig from '@app/pages/authManager/components/readRadiusConfig.component';
import ReadOauth2Config from '@app/pages/authManager/components/readOauth2Config.component';
import ReadOpenIDConnectConfig from '@app/pages/authManager/components/readOpenIDConnectConfig.component';
import { useParams } from 'react-router';

const AgentDetailsPage = () => {
    const [thisAgent, setThisAgent] = useState<IAgentDetail | undefined>(undefined);
    const [thisAgentClientDetails, setThisAgentClientDetails] = useState<IAgentClientDetails | undefined>(undefined);
    const [allAuthPolicies, setAllAuthPolicies] = useState<IPolicyAuth[] | undefined>(undefined);
    const [hideRedirectField, setHideRedirectField] = useState<boolean>(false);
    const [hideSyncIntervalField, setHideSyncIntervalField] = useState<boolean>(false);
    const [hideAuthPolicyField, setHideAuthPolicyField] = useState<boolean>(false);
    const [hideWLA2FATimeoutSeconds, setHideWLA2FATimeoutSeconds] = useState<boolean>(false);
    const [requiresConsent, setRequiresConsent] = useState<boolean>(false);
    const [redirectUri, setRedirectUri] = useState<string>('');
    const [policyId, setPolicyId] = useState<string>('');
    const [policyName, setPolicyName] = useState<string>('');
    const [showSecret, setShowSecret] = useState<boolean>(false);
    const [protectedConfig, setProtectedConfig] = useState<any>();

    const { id: agentId } = useParams<{ id: string }>();
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        agentsService.getAgentProfiles(agentId, genericErrorToast, handleSuccess);
        agentsService.getClientDetailsForAgent(agentId, genericErrorToast, handleSuccessClientDetails);
        policiesAuthService.getAuthPolicies(genericErrorToast, handleSuccessAuthPolicies);

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        if (thisAgent) {
            dispatch(addDetailsBreadcrumb(thisAgent.AgentName));

            switch (thisAgent.AgentType) {
                case AgentTypeEnum.webAuth:
                case AgentTypeEnum.apiAuth:
                    setHideSyncIntervalField(true);
                    setHideWLA2FATimeoutSeconds(true);
                    break;
                case AgentTypeEnum.oauth2Client:
                    setHideRedirectField(true);
                    setHideSyncIntervalField(true);
                    setHideAuthPolicyField(true);
                    setHideWLA2FATimeoutSeconds(true);
                    break;
                case AgentTypeEnum.openidConnect:
                    setHideSyncIntervalField(true);
                    setHideAuthPolicyField(true);
                    setHideWLA2FATimeoutSeconds(true);
                    break;
                case AgentTypeEnum.windowsLogon:
                    setHideRedirectField(true);
                    agentsService.getAgentProtectedConfig(thisAgent.Id, genericErrorToast, handleSuccessProtectedConfig);
                    break;
                case AgentTypeEnum.radiusServer:
                    setHideRedirectField(true);
                    setHideWLA2FATimeoutSeconds(true);
                    agentsService.getAgentProtectedConfig(thisAgent.Id, genericErrorToast, handleSuccessProtectedConfig);
                    break;
                default:
                    setHideRedirectField(false);
                    break;
            }

            handleAgentConfiguration(thisAgent.Configuration);
        }
    }, [thisAgent]);

    useEffect(() => {
        if (allAuthPolicies && allAuthPolicies.length && policyId !== '') {
            const policyChosen: any = allAuthPolicies.filter(function (item: IPolicyAuth) {
                return item.Id === policyId;
            });

            if (policyChosen && policyChosen.length) {
                setPolicyName(policyChosen[0].DisplayName);
            }
        }
    }, [allAuthPolicies, policyId]);

    const handleAgentConfiguration = (agentConfiguration: string) => {
        const config = JSON.parse(agentConfiguration);

        if (config) {
            setRedirectUri(config.RedirectUri);

            if (typeof config.RequiresConsent !== undefined) {
                setRequiresConsent(config.RequiresConsent);
            }

            if (config.PolicyId) {
                setPolicyId(config.PolicyId);
            }
        }
    };

    const handleSuccessAuthPolicies = (response: any) => {
        setAllAuthPolicies(response.data.value);
    };

    const handleSuccess = (response: any) => {
        setThisAgent(response.data);
    };

    const handleSuccessClientDetails = (response: any) => {
        setThisAgentClientDetails(response.data);
    };

    const handleSuccessProtectedConfig = (response: any) => {
        let protectedAgentConfig = JSON.parse(response.data.SerializedConfig !== undefined ? response.data.SerializedConfig : undefined);
        if (protectedAgentConfig === undefined) {
            setProtectedConfig(protectedAgentConfig);
        }
        else if (thisAgent && thisAgent.AgentType === AgentTypeEnum.windowsLogon) {
            setProtectedConfig(protectedAgentConfig.OverridePassword !== undefined ? protectedAgentConfig.OverridePassword : undefined);
        }
        else if (thisAgent && thisAgent.AgentType === AgentTypeEnum.radiusServer) {
            setProtectedConfig(protectedAgentConfig.Clients !== undefined ? protectedAgentConfig.Clients : undefined);
        }

    };

    const editAgent = (viewType: AgentViewType) => {
        if (thisAgent) {
            dispatch(getModalSwitch(true));

            setModalData({
                headerTitle: translation('EDIT_AGENT'),
                modalSwitcher: modalSwitchState,
                size: 'lg',
                form: <EditAgent
                          startingView={viewType}
                          pageRefresh={handlePageRefreshFromDialog}
                          editAgentConfig={thisAgent}
                          protectedAgentConfig={protectedConfig}
                          authPolicies={allAuthPolicies}
                          clientId={thisAgentClientDetails?.ClientId ? thisAgentClientDetails.ClientId : ''}
                          clientRoles={thisAgentClientDetails?.Roles ? thisAgentClientDetails.Roles : []}/>
});
}
};

const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            agentsService.getAgentProfiles(agentId, genericErrorToast, handleSuccess);

            if (thisAgent?.AgentType === AgentTypeEnum.oauth2Client) {
                agentsService.getClientDetailsForAgent(agentId, genericErrorToast, handleSuccessClientDetails);
            }
        }
    };

const showSecretKey = () => {
        setShowSecret(true);
    };

return (
<>
<ModalComponent {...modalData}/>
<div className='content--narrow detailPage'>
    <div className='detailPage__section'>
        <div className='detailPage__section-head'>
            {translation('AGENT_DETAILS')}
        </div>
        {thisAgent &&
                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                <li>
                                    <a id='editAgentLink' className='detailPage__list--primary-label' onClick={() => editAgent(AgentViewType.editAgentView)}>{translation('EDIT')}</a>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_NAME')}</span>
                                    <span className='detailPage__list--secondary-label'>{thisAgent.AgentName}</span>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_STATE')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {/*TODO: it should be fixed on the backend one day*/}
                                        {thisAgent.ProvisioningState === 'Disabled' ? translation('PROVISION_STATE_INACTIVE') : thisAgent.ProvisioningState}
                                    </span>

                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_TYPE')}</span>
                                    <span className='detailPage__list--secondary-label'>{thisAgent.AgentType}</span>
                                </li>
                                {!hideRedirectField &&
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AGENT_REQUIRES_CONSENT')}</span>
                                        <span className='detailPage__list--secondary-label'>{translation(booleanToYesNo(requiresConsent))}</span>
                                    </li>
                                }
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('AGENT_CLIENT_ID')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {thisAgent.ClientId}
                                        <div className='detailPage__inline--button'>
                                            <CopyComponent valueToCopy={thisAgent.ClientId} />
                                        </div>
                                    </span>

                                </li>
                                {thisAgentClientDetails && showSecret ?
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AGENT_CLIENT_KEY')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {thisAgentClientDetails.ClientSecretValue}
                                            <div className='detailPage__inline--button'>
                                                <CopyComponent valueToCopy={thisAgentClientDetails.ClientSecretValue} />
                                            </div>
                                        </span>
                                    </li>
                                    :
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AGENT_CLIENT_KEY')}</span>
                                        <span id='showKeySpan' className='detailPage__list--secondary-label link' onClick={showSecretKey}>{translation('SHOW_KEY')}</span>
                                    </li>
                                }
                                {redirectUri !== '' && !hideRedirectField &&
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AGENT_REDIRECT_URI')}</span>
                                        <span className='detailPage__list--secondary-label'>{redirectUri}</span>
                                    </li>
                                }
                                {!hideSyncIntervalField &&
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('SYNC_INTERVAL')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisAgent.SyncIntervalMinutes / 60}</span>
                                    </li>
                                }
                                {!hideAuthPolicyField &&
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('APP_AUTH_POLICY')}</span>
                                        {policyName !== '' ?
                                            <span className='detailPage__list--secondary-label'>{policyName}</span>
                                            :
                                            <span className='detailPage__list--secondary-label'>{translation('UNKNOWN_POLICY')}</span>
                                        }
                                    </li>
                                }
                                {!hideWLA2FATimeoutSeconds &&
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('APP_WLA_2FA_TIMEOUT')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisAgent.WLA2FATimeoutSeconds || 180}</span>
                                    </li>
                                }

                                {thisAgent.DownloadLocation !== '' &&
                                    <PasslyButton
                                        className='detailPage__button-download'
                                        variant='contained'
                                        color='primary'
                                        href={thisAgent.DownloadLocation}
                                        download>{translation('DOWNLOAD_INSTALLER')}</PasslyButton>
                                }
                            </ul>
                        </div>
                    }
    </div>

    {thisAgent !== undefined && thisAgent.AgentType === AgentTypeEnum.windowsLogon &&
                    <ReadWindowsLogon
                        agent={thisAgent}
                        editAgentCallback={editAgent}
                        windowsConfig={JSON.parse(thisAgent.Configuration) as IWindowsLogonConfig} />
                }
    {thisAgent !== undefined && thisAgent.AgentType === AgentTypeEnum.radiusServer &&
                    <ReadRadiusConfig
                        agent={thisAgent}
                        editAgentCallback={editAgent}
                        radiusConfig={JSON.parse(thisAgent.Configuration) as any}
                        radiusClients={protectedConfig === undefined ? [] as IRadiusClient[] : protectedConfig} />
                }
    {thisAgent !== undefined && thisAgent.AgentType === AgentTypeEnum.oauth2Client &&
                    <ReadOauth2Config
                        agent={thisAgent}
                        editAgentCallback={editAgent}
                        oauth2Config={thisAgentClientDetails?.Roles ? thisAgentClientDetails?.Roles : []} />
                }
    {thisAgent !== undefined && thisAgent.AgentType === AgentTypeEnum.openidConnect &&
                    <ReadOpenIDConnectConfig
                        agent={thisAgent}
                        editAgentCallback={editAgent}
                    />
                }
</div>
</>
);
};

export default AgentDetailsPage;